import type { ExerciseTemplate } from '@yuan-math/exercises';
import { rawFraction } from '@yuan-math/math';
import {
  choiceExercise,
  fractionExercise,
  fractionTrap,
  numericExercise,
  options,
  template,
  trap,
} from './authoring.ts';

/**
 * Names the size of one part: "one third", "one twelfth".
 *
 * Spelled out because `${n}th` renders as "one 3th" and "one 20th", and a
 * lesson that cannot spell its own fractions is not one a parent will trust.
 * Beyond twelfths nobody says the word, so the caller phrases it another way.
 */
const PART_NAMES = [
  '', 'whole', 'half', 'third', 'quarter', 'fifth', 'sixth',
  'seventh', 'eighth', 'ninth', 'tenth', 'eleventh', 'twelfth',
];
const partName = (denominator: number, plural = false): string => {
  const name = PART_NAMES[denominator] ?? `${denominator}th`;
  return plural ? `${name}s` : name;
};

/**
 * The rest of Grade 3: multiplication strategy, patterns, two-step problems,
 * fractions as numbers, the measured world, first data displays and shape.
 *
 * Two habits run through the file. Every template's distractors are the errors
 * a Grade 3 teacher actually sees — the dropped partial product, the numeral on
 * the clock read as a number of minutes, the picture-graph key ignored — rather
 * than plausible-looking numbers. And no template asks for an answer whose
 * units it has not put in the prompt, because at this age "8" and "8 cm" are
 * different answers to different questions.
 */
export const GRADE_THREE_TEMPLATES: ExerciseTemplate[] = [
  /* ------------------------------------- properties of multiplication ---- */
  template({
    id: 'distributive-break-apart',
    conceptId: 'multiplication-properties',
    description: 'Multiply by splitting a two-digit factor into tens and ones.',
    difficulties: [2, 3, 4],
    build: ({ rng, difficulty }) => {
      const small = rng.int(3, difficulty === 2 ? 6 : 9);
      const tens = difficulty === 4 ? rng.int(2, 4) * 10 : 10;
      // A split with no ones left over is not a split, so the ones digit is
      // never zero — otherwise the "hard" step of the strategy disappears.
      const ones = rng.int(2, 9);
      const big = tens + ones;
      return numericExercise({
        prompt: `Multiply by breaking ${big} into ${tens} and ${ones}.`,
        latex: `${small} \\times ${big}`,
        value: small * big,
        summary: `${small} × ${tens} = ${small * tens} and ${small} × ${ones} = ${small * ones}; together that is ${small * big}.`,
        hint: `Both pieces get multiplied by ${small}. Do them one at a time, then add.`,
        steps: [
          { label: 'Split the larger factor.', latex: `${small} \\times (${tens} + ${ones})` },
          { label: 'Multiply each piece.', latex: `${small} \\times ${tens} = ${small * tens}` },
          { label: 'And the other piece.', latex: `${small} \\times ${ones} = ${small * ones}` },
          { label: 'Add the two products.', latex: `${small * tens} + ${small * ones} = ${small * big}` },
        ],
        traps: [
          trap(small * tens + ones, 'partial-product-dropped'),
          trap(small * tens, 'partial-product-dropped'),
        ],
      });
    },
  }),

  template({
    id: 'commutative-missing-factor',
    conceptId: 'multiplication-properties',
    description: 'Use the commutative property to fill in a missing factor.',
    difficulties: [1, 2],
    build: ({ rng, difficulty }) => {
      const ceiling = difficulty === 1 ? 7 : 12;
      const a = rng.int(2, ceiling);
      // Equal factors would make the missing number obvious for the wrong
      // reason: the student could copy rather than reason about order.
      let b = rng.int(2, ceiling);
      if (b === a) b = a === ceiling ? a - 1 : a + 1;
      return numericExercise({
        prompt: 'Fill in the missing number.',
        latex: `${a} \\times ${b} = \\square \\times ${a}`,
        value: b,
        summary: `Swapping the order never changes a product, so the missing number is ${b}.`,
        hint: 'Both sides must have the same two factors. Which one is missing?',
        steps: [
          { label: 'The two sides use the same factors.', latex: `${a} \\times ${b} = ${b} \\times ${a}` },
          { label: 'So the box holds the other factor.', latex: String(b) },
        ],
        traps: [trap(a * b, 'factor-order-changes-product'), trap(a, 'factor-order-changes-product')],
      });
    },
  }),

  template({
    id: 'associative-three-factors',
    conceptId: 'multiplication-properties',
    description: 'Multiply three factors, regrouping to make the work easy.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const a = rng.int(2, 5);
      const b = rng.int(2, difficulty === 3 ? 5 : 6);
      const c = rng.int(2, difficulty === 3 ? 6 : 9);
      return numericExercise({
        prompt: 'Multiply.',
        latex: `${a} \\times ${b} \\times ${c}`,
        value: a * b * c,
        summary: `Grouping the easy pair first: ${b} × ${c} = ${b * c}, then ${a} × ${b * c} = ${a * b * c}.`,
        hint: 'You may multiply any two of them first — pick the pair you find easiest.',
        steps: [
          { label: 'Group a pair.', latex: `${a} \\times (${b} \\times ${c})` },
          { label: 'Multiply inside the brackets.', latex: `${b} \\times ${c} = ${b * c}` },
          { label: 'Multiply by what is left.', latex: `${a} \\times ${b * c} = ${a * b * c}` },
        ],
        traps: [
          trap(a + b + c, 'multiply-then-add-wrong-order'),
          // Two traps that land on the same number would give one wrong answer
          // two different diagnoses, so the second only appears when it differs.
          ...(a * b !== a + b + c ? [trap(a * b, 'partial-product-dropped')] : []),
        ],
      });
    },
  }),

  /* -------------------------------------------- multiplying by tens ------ */
  template({
    id: 'times-multiple-of-ten',
    conceptId: 'multiply-by-tens',
    description: 'Multiply a single digit by a multiple of ten or a hundred.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const digit = rng.int(2, 9);
      const lead = rng.int(2, 9);
      const scale = difficulty === 2 ? 10 : 100;
      const multiple = lead * scale;
      return numericExercise({
        prompt: 'Multiply.',
        latex: `${digit} \\times ${multiple}`,
        value: digit * multiple,
        summary: `${digit} × ${lead} = ${digit * lead}, and ${multiple} is ${lead} × ${scale}, so the answer is ${digit * multiple}.`,
        hint: 'Multiply the front digits first, then put the zeros back.',
        steps: [
          { label: 'Multiply the leading digits.', latex: `${digit} \\times ${lead} = ${digit * lead}` },
          { label: 'Restore the zeros you set aside.', latex: `${digit * lead} \\times ${scale} = ${digit * multiple}` },
        ],
        traps: [
          trap(digit * lead, 'trailing-zeros-dropped'),
          trap(digit * lead * (scale / 10), 'trailing-zeros-dropped'),
        ],
      });
    },
  }),

  template({
    id: 'tens-times-tens',
    conceptId: 'multiply-by-tens',
    description: 'Multiply two multiples of ten.',
    difficulties: [4, 5],
    build: ({ rng, difficulty }) => {
      const a = rng.int(2, difficulty === 4 ? 6 : 9);
      const b = rng.int(2, difficulty === 4 ? 6 : 9);
      return numericExercise({
        prompt: 'Multiply.',
        latex: `${a * 10} \\times ${b * 10}`,
        value: a * b * 100,
        summary: `${a} × ${b} = ${a * b}, and there are two tens to put back, so the answer is ${a * b * 100}.`,
        hint: 'Two tens multiplied together make a hundred.',
        steps: [
          { label: 'Multiply the leading digits.', latex: `${a} \\times ${b} = ${a * b}` },
          { label: 'Ten times ten is a hundred.', latex: `${a * b} \\times 100 = ${a * b * 100}` },
        ],
        traps: [trap(a * b * 10, 'trailing-zeros-dropped'), trap(a * b, 'trailing-zeros-dropped')],
      });
    },
  }),

  template({
    id: 'multiple-of-ten-missing-factor',
    conceptId: 'multiply-by-tens',
    description: 'Find the missing factor in a multiplication by a multiple of ten.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const missing = rng.int(2, difficulty === 3 ? 7 : 9);
      const lead = rng.int(2, 9);
      const multiple = lead * 10;
      const product = missing * multiple;
      return numericExercise({
        prompt: 'Fill in the missing number.',
        latex: `\\square \\times ${multiple} = ${product}`,
        value: missing,
        summary: `${missing} × ${multiple} = ${product}, so the missing factor is ${missing}.`,
        hint: `How many tens is ${product}? Then ask how many ${lead}s that is.`,
        steps: [
          { label: 'Take off a ten from each side.', latex: `\\square \\times ${lead} = ${product / 10}` },
          { label: 'Now it is a fact you know.', latex: `${missing} \\times ${lead} = ${product / 10}` },
        ],
        traps: [trap(product / 10, 'trailing-zeros-dropped'), trap(product - multiple, 'unknown-factor-added')],
      });
    },
  }),

  /* ------------------------------------------------- fact families ------- */
  template({
    id: 'missing-factor',
    conceptId: 'fact-families',
    description: 'Find the unknown factor in a multiplication fact.',
    difficulties: [1, 2, 3],
    build: ({ rng, difficulty }) => {
      const ceiling = difficulty === 1 ? 5 : difficulty === 2 ? 9 : 12;
      const known = rng.int(2, ceiling);
      const missing = rng.int(2, ceiling);
      const product = known * missing;
      return numericExercise({
        prompt: 'Fill in the missing number.',
        latex: `${known} \\times \\square = ${product}`,
        value: missing,
        summary: `${product} ÷ ${known} = ${missing}, so the missing factor is ${missing}.`,
        hint: `Ask yourself: ${known} times what gives ${product}?`,
        steps: [
          { label: 'Read the same fact as a division.', latex: `${product} \\div ${known} = \\square` },
          { label: 'Divide.', latex: String(missing) },
        ],
        traps: [trap(product - known, 'unknown-factor-added'), trap(product + known, 'unknown-factor-added')],
      });
    },
  }),

  template({
    id: 'fact-family-member',
    conceptId: 'fact-families',
    description: 'Pick the division fact belonging to a given multiplication fact.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const a = rng.int(2, difficulty === 2 ? 7 : 12);
      const b = rng.int(2, difficulty === 2 ? 7 : 12);
      const product = a * b;
      const { choices, correctId } = options(
        [
          { latex: `${product} \\div ${a} = ${b}`, correct: true },
          { latex: `${product} - ${a} = ${product - a}`, misconceptionId: 'unknown-factor-added' },
          { latex: `${a} + ${b} = ${a + b}`, misconceptionId: 'unknown-factor-added' },
          { latex: `${a} \\div ${product} = ${b}`, misconceptionId: 'divide-wrong-direction' },
        ],
        rng.shuffle,
      );
      return choiceExercise({
        prompt: `Which fact belongs to the same family as ${a} × ${b} = ${product}?`,
        choices,
        correctId,
        summary: `A family holds two multiplications and two divisions: ${a} × ${b}, ${b} × ${a}, ${product} ÷ ${a} and ${product} ÷ ${b}.`,
        hint: 'The same three numbers must appear, and the largest one is the product.',
        steps: [
          { label: 'Name the three numbers.', latex: `${a}, \\; ${b}, \\; ${product}` },
          { label: 'The product is what gets divided.', latex: `${product} \\div ${a} = ${b}` },
        ],
      });
    },
  }),

  template({
    id: 'missing-divisor',
    conceptId: 'fact-families',
    description: 'Find the unknown divisor in a division fact.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const divisor = rng.int(2, difficulty === 3 ? 9 : 12);
      const quotient = rng.int(2, difficulty === 3 ? 9 : 12);
      const dividend = divisor * quotient;
      return numericExercise({
        prompt: 'Fill in the missing number.',
        latex: `${dividend} \\div \\square = ${quotient}`,
        value: divisor,
        summary: `${divisor} × ${quotient} = ${dividend}, so the missing divisor is ${divisor}.`,
        hint: `What times ${quotient} gives ${dividend}?`,
        steps: [
          { label: 'Read the division as a multiplication.', latex: `\\square \\times ${quotient} = ${dividend}` },
          { label: 'Recall the fact.', latex: `${divisor} \\times ${quotient} = ${dividend}` },
        ],
        traps: [trap(dividend - quotient, 'unknown-factor-added'), trap(dividend * quotient, 'divide-wrong-direction')],
      });
    },
  }),

  /* ---------------------------------------------- patterns in numbers ---- */
  template({
    id: 'extend-sequence',
    conceptId: 'number-patterns',
    description: 'Continue a sequence by finding its rule.',
    difficulties: [1, 2, 3],
    build: ({ rng, difficulty }) => {
      if (difficulty === 3) {
        const start = rng.int(2, 6);
        const terms = [start, start * 2, start * 4, start * 8];
        const answer = start * 16;
        const last = terms[3] as number;
        return numericExercise({
          prompt: 'What comes next in this pattern?',
          latex: `${terms.join(', \\; ')}, \\; \\square`,
          value: answer,
          summary: `Each term is double the one before, so after ${last} comes ${answer}.`,
          hint: 'Compare each term with the one before it. Is it being added to, or multiplied?',
          steps: [
            { label: 'Check the rule against every step.', latex: `${terms[0]} \\times 2 = ${terms[1]}` },
            { label: 'Apply it once more.', latex: `${last} \\times 2 = ${answer}` },
          ],
          traps: [trap(last + (last - (terms[2] as number)), 'pattern-rule-misread')],
        });
      }
      const start = rng.int(1, difficulty === 1 ? 6 : 20);
      const step = rng.int(2, difficulty === 1 ? 5 : 12);
      const terms = [0, 1, 2, 3].map((i) => start + i * step);
      const answer = start + 4 * step;
      return numericExercise({
        prompt: 'What comes next in this pattern?',
        latex: `${terms.join(', \\; ')}, \\; \\square`,
        value: answer,
        summary: `Each term is ${step} more than the one before, so after ${terms[3]} comes ${answer}.`,
        hint: 'Work out the gap between the first two terms, then check it fits the rest.',
        steps: [
          { label: 'Find the gap.', latex: `${terms[1]} - ${terms[0]} = ${step}` },
          { label: 'Check it holds all the way along.', latex: `${terms[3]} - ${terms[2]} = ${step}` },
          { label: 'Add one more gap.', latex: `${terms[3]} + ${step} = ${answer}` },
        ],
        traps: [trap(answer + 1, 'pattern-rule-misread'), trap((terms[3] as number) + step + step, 'pattern-rule-misread')],
      });
    },
  }),

  template({
    id: 'pattern-rule-choice',
    conceptId: 'number-patterns',
    description: 'Name the rule that generates a sequence.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const start = rng.int(2, difficulty === 2 ? 9 : 24);
      const step = rng.int(3, difficulty === 2 ? 9 : 12);
      const terms = [0, 1, 2, 3].map((i) => start + i * step);
      const { choices, correctId } = options(
        [
          { label: `Add ${step}`, correct: true },
          { label: `Add ${step + 1}`, misconceptionId: 'pattern-rule-misread' },
          { label: `Multiply by ${step}`, misconceptionId: 'pattern-rule-misread' },
          { label: `Subtract ${step}`, misconceptionId: 'pattern-rule-misread' },
        ],
        rng.shuffle,
      );
      return choiceExercise({
        prompt: `What is the rule for this pattern: ${terms.join(', ')}?`,
        choices,
        correctId,
        summary: `Every step goes up by ${step}, and the terms are growing, so the rule is "add ${step}".`,
        hint: 'Test your rule on every pair of neighbours, not just the first.',
        steps: [
          { label: 'First gap.', latex: `${terms[1]} - ${terms[0]} = ${step}` },
          { label: 'Last gap — the same.', latex: `${terms[3]} - ${terms[2]} = ${step}` },
        ],
      });
    },
  }),

  template({
    id: 'product-parity',
    conceptId: 'number-patterns',
    description: 'Decide whether a product is odd or even.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const ceiling = difficulty === 2 ? 9 : 12;
      const a = rng.int(2, ceiling);
      const b = rng.int(2, ceiling);
      const even = (a * b) % 2 === 0;
      const { choices, correctId } = options(
        [
          { label: 'Even', correct: even, ...(even ? {} : { misconceptionId: 'pattern-rule-misread' }) },
          { label: 'Odd', correct: !even, ...(even ? { misconceptionId: 'pattern-rule-misread' } : {}) },
        ],
        rng.shuffle,
      );
      return choiceExercise({
        prompt: `Without working it out, is ${a} × ${b} odd or even?`,
        choices,
        correctId,
        summary: even
          ? 'One of the factors is even, and an even number of anything can still be split into twos.'
          : 'Both factors are odd, and odd groups of odd things leave one over — so the product is odd.',
        hint: 'A product is even as soon as *one* of its factors is even.',
        steps: [
          { label: 'Look at the factors.', latex: `${a} \\text{ and } ${b}` },
          { label: even ? 'At least one is even.' : 'Both are odd.', latex: `${a} \\times ${b} = ${a * b}` },
        ],
      });
    },
  }),

  /* -------------------------------------------- two-step word problems --- */
  template({
    id: 'two-step-multiply-add',
    conceptId: 'two-step-problems',
    description: 'Multiply, then add — with the first answer offered as a trap.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const groups = rng.int(3, difficulty === 2 ? 6 : 9);
      const per = rng.int(4, difficulty === 2 ? 8 : 12);
      const extra = rng.int(2, difficulty === 2 ? 9 : 20);
      const scenario = rng.pick([
        {
          text: `Maya buys ${groups} boxes of pencils with ${per} pencils in each box. She already had ${extra} pencils at home.`,
          question: 'How many pencils does she have now?',
        },
        {
          text: `A baker fills ${groups} trays with ${per} rolls each, and then bakes ${extra} more rolls.`,
          question: 'How many rolls has the baker made?',
        },
        {
          text: `There are ${groups} shelves holding ${per} books each, and ${extra} books are stacked on the floor.`,
          question: 'How many books are there altogether?',
        },
      ]);
      return numericExercise({
        prompt: `${scenario.text} ${scenario.question}`,
        value: groups * per + extra,
        summary: `${groups} × ${per} = ${groups * per}, and ${groups * per} + ${extra} = ${groups * per + extra}.`,
        hint: 'Work out the equal groups first. That answer is not the final one.',
        steps: [
          { label: 'The equal groups.', latex: `${groups} \\times ${per} = ${groups * per}` },
          { label: 'Then add what was already there.', latex: `${groups * per} + ${extra} = ${groups * per + extra}` },
        ],
        traps: [
          trap(groups * per, 'intermediate-answer-given'),
          trap(groups * (per + extra), 'multiply-then-add-wrong-order'),
        ],
      });
    },
  }),

  template({
    id: 'two-step-subtract-divide',
    conceptId: 'two-step-problems',
    description: 'Subtract, then share equally.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const groups = rng.int(3, difficulty === 3 ? 6 : 9);
      const each = rng.int(3, difficulty === 3 ? 8 : 12);
      const removed = rng.int(2, 15);
      const total = groups * each + removed;
      const scenario = rng.pick([
        {
          text: `A class has ${total} crayons. ${removed} are broken and thrown away. The rest are shared equally between ${groups} tables.`,
          question: 'How many crayons does each table get?',
        },
        {
          text: `A farmer picks ${total} apples. ${removed} are bruised and set aside. The rest are packed equally into ${groups} crates.`,
          question: 'How many apples go in each crate?',
        },
      ]);
      return numericExercise({
        prompt: `${scenario.text} ${scenario.question}`,
        value: each,
        summary: `${total} − ${removed} = ${groups * each}, and ${groups * each} ÷ ${groups} = ${each}.`,
        hint: 'Take away first, and share what is left — not what you started with.',
        steps: [
          { label: 'Take away the ones that are gone.', latex: `${total} - ${removed} = ${groups * each}` },
          { label: 'Share the rest equally.', latex: `${groups * each} \\div ${groups} = ${each}` },
        ],
        traps: [
          trap(groups * each, 'intermediate-answer-given'),
          trap(total - removed - groups, 'unknown-factor-added'),
        ],
      });
    },
  }),

  template({
    id: 'two-step-divide-multiply',
    conceptId: 'two-step-problems',
    description: 'Share equally, then multiply the share.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const teams = rng.int(3, difficulty === 2 ? 5 : 8);
      const perTeam = rng.int(3, difficulty === 2 ? 6 : 9);
      const each = rng.int(2, difficulty === 2 ? 5 : 9);
      const children = teams * perTeam;
      return numericExercise({
        prompt: `${children} children are split equally into ${teams} teams. Every child in a team is given ${each} counters. How many counters does one team receive?`,
        value: perTeam * each,
        summary: `${children} ÷ ${teams} = ${perTeam} children per team, and ${perTeam} × ${each} = ${perTeam * each} counters.`,
        hint: 'How big is one team? Only then can you count its counters.',
        steps: [
          { label: 'Find the size of one team.', latex: `${children} \\div ${teams} = ${perTeam}` },
          { label: 'Give each child their counters.', latex: `${perTeam} \\times ${each} = ${perTeam * each}` },
        ],
        traps: [
          trap(perTeam, 'intermediate-answer-given'),
          trap(children * each, 'divide-wrong-direction'),
        ],
      });
    },
  }),

  /* ------------------------------------ fractions on the number line ----- */
  template({
    id: 'read-fraction-on-line',
    conceptId: 'fractions-on-number-line',
    description: 'Name the fraction marked on a number line between 0 and 1.',
    difficulties: [1, 2, 3],
    build: ({ rng, difficulty }) => {
      const denominator = rng.pick(difficulty === 1 ? [2, 3, 4] : difficulty === 2 ? [4, 5, 6] : [6, 8, 10]);
      const numerator = rng.int(1, denominator - 1);
      return fractionExercise({
        prompt: 'What fraction is marked on the number line?',
        figure: {
          kind: 'number-line',
          from: 0,
          to: 1,
          step: 1 / denominator,
          labelAs: 'fraction',
          marks: [{ at: numerator / denominator, label: '?' }],
        },
        value: rawFraction(numerator, denominator),
        summary: `The line from 0 to 1 is cut into ${denominator} equal parts, and the mark is ${numerator} of them along.`,
        hint: 'Count the equal gaps between 0 and 1 — that number goes underneath.',
        steps: [
          { label: 'Count the gaps from 0 to 1.', latex: String(denominator) },
          { label: 'Count the gaps up to the mark.', latex: String(numerator) },
          { label: 'Write it as a fraction.', latex: `\\frac{${numerator}}{${denominator}}` },
        ],
        traps: [
          fractionTrap(rawFraction(numerator, denominator + 1), 'number-line-ticks-counted'),
          fractionTrap(rawFraction(numerator + 1, denominator + 1), 'number-line-ticks-counted'),
        ],
      });
    },
  }),

  template({
    id: 'count-equal-parts',
    conceptId: 'fractions-on-number-line',
    description: 'Name the size of one part when a unit is divided equally.',
    difficulties: [1, 2],
    build: ({ rng, difficulty }) => {
      const denominator = rng.int(2, difficulty === 1 ? 6 : 11);
      const context = rng.pick([
        'A number line from 0 to 1',
        'The distance from 0 to 1 on a number line',
        'A ribbon one metre long',
        'A journey of one kilometre',
      ]);
      return fractionExercise({
        prompt: `${context} is divided into ${denominator} equal parts. What fraction of the whole is one part?`,
        figure: {
          kind: 'number-line',
          from: 0,
          to: 1,
          step: 1 / denominator,
          labelAs: 'fraction',
        },
        value: rawFraction(1, denominator),
        summary: `${denominator} equal parts make one whole, so each part is one ${partName(denominator)}.`,
        hint: 'The number of equal parts is the denominator; one part is one of them.',
        steps: [
          { label: 'Count the equal parts.', latex: String(denominator) },
          { label: 'One of them.', latex: `\\frac{1}{${denominator}}` },
        ],
        traps: [
          fractionTrap(rawFraction(1, denominator + 1), 'number-line-ticks-counted'),
          fractionTrap(rawFraction(denominator, 1), 'whole-number-fraction-confused'),
        ],
      });
    },
  }),

  template({
    id: 'fraction-beyond-one',
    conceptId: 'fractions-on-number-line',
    description: 'Name a fraction marked past 1 on a number line.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const denominator = rng.pick(difficulty === 3 ? [2, 3, 4] : [4, 5, 6]);
      const numerator = rng.int(denominator + 1, 2 * denominator - 1);
      return fractionExercise({
        prompt: 'What fraction is marked on the number line?',
        figure: {
          kind: 'number-line',
          from: 0,
          to: 2,
          step: 1 / denominator,
          labelAs: 'fraction',
          marks: [{ at: numerator / denominator, label: '?' }],
        },
        value: rawFraction(numerator, denominator),
        summary: `Each gap is one ${partName(denominator)}, and the mark is ${numerator} gaps from 0 — past 1, but still counted from 0.`,
        hint: 'Keep counting the same size gaps after you pass 1. Do not start again.',
        steps: [
          { label: 'One whole is this many parts.', latex: `\\frac{${denominator}}{${denominator}} = 1` },
          { label: 'The mark is further along.', latex: `\\frac{${numerator}}{${denominator}}` },
        ],
        traps: [fractionTrap(rawFraction(numerator - denominator, denominator), 'number-line-ticks-counted')],
      });
    },
  }),

  /* -------------------------------------- whole numbers as fractions ----- */
  template({
    id: 'fraction-equal-to-whole',
    conceptId: 'whole-number-fractions',
    description: 'Recognise a fraction that is really a whole number.',
    difficulties: [1, 2],
    build: ({ rng, difficulty }) => {
      const n = rng.int(2, difficulty === 1 ? 8 : 12);
      const overItself = rng.bool();
      return numericExercise({
        prompt: overItself
          ? `What whole number is ${n}/${n} equal to?`
          : `What whole number is ${n}/1 equal to?`,
        latex: overItself ? `\\frac{${n}}{${n}}` : `\\frac{${n}}{1}`,
        value: overItself ? 1 : n,
        summary: overItself
          ? `${n} parts out of ${n} is the whole thing, which is 1.`
          : `A denominator of 1 means the pieces are whole ones, and there are ${n} of them.`,
        hint: 'The bottom number says how big each piece is; the top says how many you have.',
        steps: overItself
          ? [
              { label: 'The pieces are this size.', latex: `\\frac{1}{${n}}` },
              { label: 'Take all of them.', latex: `${n} \\times \\frac{1}{${n}} = 1` },
            ]
          : [
              { label: 'Each piece is a whole one.', latex: `\\frac{1}{1} = 1` },
              { label: 'Take this many.', latex: `${n} \\times 1 = ${n}` },
            ],
        traps: [trap(overItself ? n : 1, 'whole-number-fraction-confused')],
      });
    },
  }),

  template({
    id: 'whole-as-fraction',
    conceptId: 'whole-number-fractions',
    description: 'Write a whole number as a fraction with a given denominator.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const whole = rng.int(2, difficulty === 2 ? 6 : 9);
      const denominator = rng.int(2, difficulty === 2 ? 5 : 8);
      return fractionExercise({
        prompt: `Write ${whole} as a fraction with denominator ${denominator}.`,
        value: rawFraction(whole * denominator, denominator),
        requireExactForm: true,
        summary: `Each whole holds ${denominator} of these parts, and there are ${whole} wholes: ${whole * denominator} parts in all.`,
        hint: `How many ${partName(denominator, true)} make one whole? Now you need ${whole} lots of that.`,
        steps: [
          { label: 'One whole in these parts.', latex: `1 = \\frac{${denominator}}{${denominator}}` },
          { label: 'Take that many wholes.', latex: `${whole} \\times ${denominator} = ${whole * denominator}` },
          { label: 'Write the fraction.', latex: `\\frac{${whole * denominator}}{${denominator}}` },
        ],
        traps: [
          fractionTrap(rawFraction(denominator, whole * denominator), 'whole-number-fraction-confused'),
          fractionTrap(rawFraction(whole, denominator), 'whole-number-fraction-confused'),
        ],
      });
    },
  }),

  template({
    id: 'whole-on-number-line',
    conceptId: 'whole-number-fractions',
    description: 'Name a whole-number point on a number line divided into fractions.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const denominator = rng.pick(difficulty === 3 ? [2, 3] : [3, 4, 5]);
      const whole = rng.int(1, 2);
      return fractionExercise({
        prompt: `Write the marked point as a fraction with denominator ${denominator}.`,
        figure: {
          kind: 'number-line',
          from: 0,
          to: 2,
          step: 1 / denominator,
          labelAs: 'fraction',
          marks: [{ at: whole, label: '?' }],
        },
        value: rawFraction(whole * denominator, denominator),
        requireExactForm: true,
        summary: `The mark sits on ${whole}, and each whole is ${denominator} of these parts, so it is ${whole * denominator} of them.`,
        hint: 'Count the small gaps from 0 all the way to the mark.',
        steps: [
          { label: 'Count the gaps in one whole.', latex: String(denominator) },
          { label: 'Count all the gaps to the mark.', latex: String(whole * denominator) },
          { label: 'Write the fraction.', latex: `\\frac{${whole * denominator}}{${denominator}}` },
        ],
        traps: [fractionTrap(rawFraction(whole, denominator), 'whole-number-fraction-confused')],
      });
    },
  }),

  /* ---------------------------------------------------- telling time ----- */
  template({
    id: 'read-clock-face',
    conceptId: 'telling-time',
    description: 'Read a time from an analogue clock face.',
    difficulties: [1, 2, 3],
    build: ({ rng, difficulty }) => {
      const hour = rng.int(1, 12);
      // Never on the hour: with both hands together the swapped-hands reading
      // and the correct one coincide, and the distractor stops teaching.
      const numeral = difficulty === 1 ? rng.pick([3, 6, 9]) : rng.int(1, 11);
      const minute = numeral * 5;
      const nextHour = hour === 12 ? 1 : hour + 1;
      const swappedHour = numeral;
      const swappedMinute = (hour % 12) * 5;
      const show = (h: number, m: number): string => `${h}:${String(m).padStart(2, '0')}`;

      const entries = [
        { label: show(hour, minute), correct: true },
        { label: show(swappedHour, swappedMinute), misconceptionId: 'clock-hands-swapped' },
        { label: show(hour, numeral), misconceptionId: 'minutes-not-multiplied' },
        { label: show(nextHour, minute) },
      ];
      // Two identical labels would make one of them silently unselectable.
      const seen = new Set<string>();
      const unique = entries.filter((entry) => {
        if (seen.has(entry.label)) return false;
        seen.add(entry.label);
        return true;
      });
      const { choices, correctId } = options(unique, rng.shuffle);

      return choiceExercise({
        prompt: 'What time does this clock show?',
        figure: { kind: 'clock', hour, minute },
        choices,
        correctId,
        summary: `The short hand has passed ${hour}, and the long hand is on the ${numeral}, which is ${minute} minutes past.`,
        hint: 'Find the short hand first — it names the hour it has already passed.',
        steps: [
          { label: 'The short hand has passed this numeral.', latex: String(hour) },
          { label: 'The long hand is on this numeral.', latex: String(numeral) },
          { label: 'Each numeral is five minutes.', latex: `${numeral} \\times 5 = ${minute}` },
        ],
      });
    },
  }),

  template({
    id: 'minutes-past-the-hour',
    conceptId: 'telling-time',
    description: 'Convert the minute hand’s position into minutes.',
    difficulties: [1, 2],
    build: ({ rng, difficulty }) => {
      const hour = rng.int(1, 12);
      const numeral = difficulty === 1 ? rng.pick([1, 2, 3, 4, 6]) : rng.int(1, 11);
      const minute = numeral * 5;
      return numericExercise({
        prompt: `The long hand points at the ${numeral}. How many minutes past the hour is it?`,
        figure: { kind: 'clock', hour, minute },
        unit: 'minutes',
        value: minute,
        summary: `Each numeral is five minutes further round, so ${numeral} × 5 = ${minute} minutes past.`,
        hint: 'Count round in fives, one five for each numeral you pass.',
        steps: [
          { label: 'Count the numerals passed.', latex: String(numeral) },
          { label: 'Five minutes each.', latex: `${numeral} \\times 5 = ${minute}` },
        ],
        traps: [trap(numeral, 'minutes-not-multiplied')],
      });
    },
  }),

  template({
    id: 'minutes-shown-on-clock',
    conceptId: 'telling-time',
    description: 'Read the number of minutes past the hour from a clock face.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const hour = rng.int(1, 12);
      const minute = difficulty === 2 ? rng.int(1, 11) * 5 : rng.int(1, 59);
      const swapped = (hour % 12) * 5;
      // The swapped-hands answer only teaches something when it is a different
      // number from the right one — and from the other trap.
      const numeralReading = Math.round(minute / 5);
      return numericExercise({
        prompt: `How many minutes past ${hour} o'clock does this clock show?`,
        figure: { kind: 'clock', hour, minute },
        unit: 'minutes',
        value: minute,
        summary: `The long hand is ${minute} minutes round from the top.`,
        hint: 'Count round the small marks from 12, or count the numerals in fives.',
        steps: [
          { label: 'Start at the 12 and count round.', latex: '\\text{one mark is one minute}' },
          { label: 'Minutes past the hour.', latex: String(minute) },
        ],
        traps: [
          ...(minute % 5 === 0 && numeralReading !== minute
            ? [trap(numeralReading, 'minutes-not-multiplied')]
            : []),
          ...(swapped !== minute && swapped !== numeralReading ? [trap(swapped, 'clock-hands-swapped')] : []),
        ],
      });
    },
  }),

  /* ---------------------------------------------------- elapsed time ----- */
  template({
    id: 'elapsed-minutes',
    conceptId: 'elapsed-time',
    description: 'Find how many minutes passed between two clock times.',
    difficulties: [2, 3, 4],
    build: ({ rng, difficulty }) => {
      const startHour = rng.int(1, 10);
      const startMinute = rng.int(1, 11) * 5;
      const duration = difficulty === 2 ? rng.int(2, 11) * 5 : difficulty === 3 ? rng.int(4, 17) * 5 : rng.int(7, 29) * 5;
      const endTotal = startHour * 60 + startMinute + duration;
      const endHour = Math.floor(endTotal / 60);
      const endMinute = endTotal % 60;
      const show = (h: number, m: number): string => `${h}:${String(m).padStart(2, '0')}`;
      const decimalDifference = (endHour * 100 + endMinute) - (startHour * 100 + startMinute);
      return numericExercise({
        prompt: `A film starts at ${show(startHour, startMinute)} and finishes at ${show(endHour, endMinute)}. How many minutes long is it?`,
        unit: 'minutes',
        value: duration,
        summary: `From ${show(startHour, startMinute)} to ${show(endHour, endMinute)} is ${duration} minutes.`,
        hint: 'Count up to the next whole hour first, then count the whole hours, then the rest.',
        steps: [
          { label: 'Up to the next hour.', latex: `${60 - startMinute} \\text{ minutes}` },
          { label: 'Whole hours after that.', latex: `${Math.floor((duration - (60 - startMinute)) / 60)} \\times 60` },
          { label: 'And the last few minutes.', latex: `${duration} \\text{ minutes in all}` },
        ],
        traps: [trap(decimalDifference, 'time-treated-as-decimal')],
      });
    },
  }),

  template({
    id: 'elapsed-end-time',
    conceptId: 'elapsed-time',
    description: 'Find the finishing time given a start time and a duration.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const startHour = rng.int(1, 10);
      // A start late in the hour guarantees the crossing that the whole
      // question is about; without it, "3:75" is never even tempting.
      const startMinute = rng.int(7, 11) * 5;
      /*
       * The duration is bounded so the uncarried distractor stays a plausible
       * misreading. Unbounded, a 55-minute practice from 1:50 offers "1:105",
       * which no student would pick and which therefore diagnoses nobody: the
       * error being caught is carrying at a hundred, not at a thousand.
       */
      const lowestStep = Math.ceil((65 - startMinute) / 5);
      const highestStep = Math.floor((99 - startMinute) / 5);
      const midStep = Math.floor((lowestStep + highestStep) / 2);
      const duration =
        (difficulty === 2 ? rng.int(lowestStep, midStep) : rng.int(midStep + 1, highestStep)) * 5;
      const endTotal = startHour * 60 + startMinute + duration;
      const endHour = Math.floor(endTotal / 60);
      const endMinute = endTotal % 60;
      const show = (h: number, m: number): string => `${h}:${String(m).padStart(2, '0')}`;
      const uncarried = `${startHour}:${startMinute + duration}`;

      const entries = [
        { label: show(endHour, endMinute), correct: true },
        { label: uncarried, misconceptionId: 'time-treated-as-decimal' },
        { label: show(endHour === 12 ? 1 : endHour + 1, endMinute) },
        { label: show(startHour, endMinute) },
      ];
      const seen = new Set<string>();
      const unique = entries.filter((entry) => {
        if (seen.has(entry.label)) return false;
        seen.add(entry.label);
        return true;
      });
      const { choices, correctId } = options(unique, rng.shuffle);

      return choiceExercise({
        prompt: `Choir practice starts at ${show(startHour, startMinute)} and lasts ${duration} minutes. When does it finish?`,
        choices,
        correctId,
        summary: `${60 - startMinute} minutes reach ${startHour + 1}:00, and the remaining ${duration - (60 - startMinute)} take it to ${show(endHour, endMinute)}.`,
        hint: 'Never let the minutes pass 59 — trade sixty of them for an hour.',
        steps: [
          { label: 'Reach the next whole hour.', latex: `${60 - startMinute} \\text{ minutes}` },
          { label: 'Use up what is left of the duration.', latex: `${duration - (60 - startMinute)} \\text{ minutes}` },
          { label: 'Finishing time.', latex: `\\text{${show(endHour, endMinute)}}` },
        ],
      });
    },
  }),

  template({
    id: 'minutes-to-the-hour',
    conceptId: 'elapsed-time',
    description: 'Count the minutes remaining until the next whole hour.',
    difficulties: [1, 2],
    build: ({ rng, difficulty }) => {
      const hour = rng.int(1, 11);
      const minute = difficulty === 1 ? rng.int(1, 11) * 5 : rng.int(1, 59);
      const show = `${hour}:${String(minute).padStart(2, '0')}`;
      return numericExercise({
        prompt: `How many minutes are there from ${show} until ${hour + 1} o'clock?`,
        figure: { kind: 'clock', hour, minute },
        unit: 'minutes',
        value: 60 - minute,
        summary: `An hour holds 60 minutes, and ${minute} have gone, so ${60 - minute} are left.`,
        hint: 'How many more minutes take the long hand back round to the 12?',
        steps: [
          { label: 'An hour is this many minutes.', latex: '60' },
          { label: 'Take away the minutes already past.', latex: `60 - ${minute} = ${60 - minute}` },
        ],
        traps: [trap(100 - minute, 'time-treated-as-decimal')],
      });
    },
  }),

  /* ------------------------------------------- mass and liquid volume ---- */
  template({
    id: 'convert-metric-unit',
    conceptId: 'mass-and-capacity',
    description: 'Convert between kilograms and grams, or litres and millilitres.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const mass = rng.bool();
      const big = mass ? 'kilograms' : 'litres';
      const bigOne = mass ? 'kilogram' : 'litre';
      const small = mass ? 'grams' : 'millilitres';
      const amount = rng.int(2, 9);
      if (difficulty === 2) {
        return numericExercise({
          prompt: `How many ${small} are there in ${amount} ${big}?`,
          unit: small,
          value: amount * 1000,
          summary: `One ${bigOne} is 1000 ${small}, so ${amount} ${big} is ${amount * 1000} ${small}.`,
          hint: 'The prefix "kilo" and the prefix "milli" both point at a thousand.',
          steps: [
            { label: 'One larger unit.', latex: '1000' },
            { label: 'That many times over.', latex: `${amount} \\times 1000 = ${amount * 1000}` },
          ],
          traps: [trap(amount * 100, 'measurement-units-mixed'), trap(amount * 10, 'trailing-zeros-dropped')],
        });
      }
      return numericExercise({
        prompt: `How many ${big} are there in ${amount * 1000} ${small}?`,
        unit: big,
        value: amount,
        summary: `Every 1000 ${small} makes one ${bigOne}, so ${amount * 1000} ${small} is ${amount} ${big}.`,
        hint: 'Going to the bigger unit makes the number smaller.',
        steps: [
          { label: 'Group them in thousands.', latex: `${amount * 1000} \\div 1000` },
          { label: 'Answer.', latex: String(amount) },
        ],
        traps: [trap(amount * 1000, 'measurement-units-mixed'), trap(amount * 10, 'trailing-zeros-dropped')],
      });
    },
  }),

  template({
    id: 'mass-difference-mixed-units',
    conceptId: 'mass-and-capacity',
    description: 'Compare two masses given in different units.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const kilograms = rng.int(2, difficulty === 3 ? 5 : 9);
      const grams = rng.int(1, 19) * 50;
      const heavier = kilograms * 1000 - grams;
      return numericExercise({
        prompt: `A watermelon has a mass of ${kilograms} kilograms. A pineapple has a mass of ${grams} grams. How many grams heavier is the watermelon?`,
        unit: 'g',
        value: heavier,
        summary: `${kilograms} kg is ${kilograms * 1000} g, and ${kilograms * 1000} − ${grams} = ${heavier} g.`,
        hint: 'Put both masses in the same unit before you subtract.',
        steps: [
          { label: 'Convert to one unit.', latex: `${kilograms} \\times 1000 = ${kilograms * 1000}` },
          { label: 'Subtract.', latex: `${kilograms * 1000} - ${grams} = ${heavier}` },
        ],
        traps: [
          trap(Math.abs(kilograms - grams), 'measurement-units-mixed'),
          trap(kilograms * 1000 + grams, 'graph-total-for-difference'),
        ],
      });
    },
  }),

  template({
    id: 'capacity-portions',
    conceptId: 'mass-and-capacity',
    description: 'Work out how many small measures fill a larger one.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const litres = rng.int(2, difficulty === 3 ? 6 : 9);
      const glass = rng.pick([100, 125, 200, 250, 500]);
      return numericExercise({
        prompt: `A jug holds ${litres} litres of juice. How many ${glass} millilitre glasses can be filled from it?`,
        unit: 'glasses',
        value: (litres * 1000) / glass,
        summary: `${litres} litres is ${litres * 1000} mL, and ${litres * 1000} ÷ ${glass} = ${(litres * 1000) / glass}.`,
        hint: 'Convert the jug to millilitres first — the two measurements must share a unit.',
        steps: [
          { label: 'Convert the jug.', latex: `${litres} \\times 1000 = ${litres * 1000}` },
          { label: 'Share it into glasses.', latex: `${litres * 1000} \\div ${glass} = ${(litres * 1000) / glass}` },
        ],
        traps: [
          trap(litres * glass, 'measurement-units-mixed'),
          // Converted one litre and forgot there were several.
          trap(1000 / glass, 'intermediate-answer-given'),
        ],
      });
    },
  }),

  /* -------------------------------------------- picture and bar graphs --- */
  template({
    id: 'picture-graph-key',
    conceptId: 'scaled-graphs',
    description: 'Read a picture graph whose symbol stands for several items.',
    difficulties: [1, 2, 3],
    build: ({ rng, difficulty }) => {
      const scale = difficulty === 1 ? rng.pick([2, 5]) : difficulty === 2 ? rng.pick([4, 5, 10]) : rng.pick([5, 10, 20]);
      const symbols = rng.int(3, 9);
      const person = rng.pick(['Ana', 'Ben', 'Chidi', 'Dara', 'Eli', 'Farah']);
      const thing = rng.pick(['books', 'stickers', 'apples', 'goals', 'shells']);
      return numericExercise({
        prompt: `In a picture graph, each symbol stands for ${scale} ${thing}. ${person}'s row has ${symbols} symbols. How many ${thing} is that?`,
        unit: thing,
        value: symbols * scale,
        summary: `${symbols} symbols at ${scale} each is ${symbols} × ${scale} = ${symbols * scale}.`,
        hint: 'The key tells you what one symbol is worth. Multiply by it.',
        steps: [
          { label: 'Count the symbols.', latex: String(symbols) },
          { label: 'Multiply by the key.', latex: `${symbols} \\times ${scale} = ${symbols * scale}` },
        ],
        traps: [trap(symbols, 'graph-scale-ignored'), trap(symbols + scale, 'multiply-then-add-wrong-order')],
      });
    },
  }),

  template({
    id: 'bar-graph-difference',
    conceptId: 'scaled-graphs',
    description: 'Compare two bars on a bar graph.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const labels = rng.shuffle(['Red', 'Blue', 'Green', 'Yellow', 'Purple']).slice(0, 4);
      const ceiling = difficulty === 2 ? 12 : 40;
      const values = labels.map(() => rng.int(2, ceiling));
      const bars = labels.map((label, index) => ({ label, value: values[index] as number }));
      // Ordered so the question always reads "how many more", never a negative.
      const sorted = [...bars].sort((a, b) => b.value - a.value);
      const high = sorted[0] as { label: string; value: number };
      const low = sorted[sorted.length - 1] as { label: string; value: number };
      return numericExercise({
        prompt: `How many more people chose ${high.label} than ${low.label}?`,
        figure: { kind: 'bar-chart', bars, unitLabel: 'favourite colour' },
        value: high.value - low.value,
        summary: `${high.label} has ${high.value} and ${low.label} has ${low.value}: a difference of ${high.value - low.value}.`,
        hint: '"How many more" asks for a difference, so subtract.',
        steps: [
          { label: 'Read both bars.', latex: `${high.value} \\text{ and } ${low.value}` },
          { label: 'Subtract.', latex: `${high.value} - ${low.value} = ${high.value - low.value}` },
        ],
        traps: [trap(high.value + low.value, 'graph-total-for-difference')],
      });
    },
  }),

  template({
    id: 'bar-graph-total',
    conceptId: 'scaled-graphs',
    description: 'Total every bar on a bar graph.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const labels = rng.shuffle(['Cats', 'Dogs', 'Fish', 'Birds', 'Rabbits']).slice(0, 4);
      const ceiling = difficulty === 2 ? 9 : 25;
      const bars = labels.map((label) => ({ label, value: rng.int(2, ceiling) }));
      const total = bars.reduce((sum, bar) => sum + bar.value, 0);
      return numericExercise({
        prompt: 'How many children were asked altogether?',
        figure: { kind: 'bar-chart', bars, unitLabel: 'favourite pet' },
        value: total,
        summary: `Adding every bar: ${bars.map((bar) => bar.value).join(' + ')} = ${total}.`,
        hint: 'Every child appears in exactly one bar, so add all the bars.',
        steps: [
          { label: 'Read each bar.', latex: bars.map((bar) => bar.value).join(' + ') },
          { label: 'Add them.', latex: String(total) },
        ],
        traps: [trap(bars.length, 'graph-scale-ignored'), trap(Math.max(...bars.map((b) => b.value)), 'graph-scale-ignored')],
      });
    },
  }),

  /* ----------------------------------------------------- line plots ------ */
  template({
    id: 'line-plot-count',
    conceptId: 'line-plots',
    description: 'Count the observations at one value of a line plot.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const low = rng.int(3, difficulty === 2 ? 6 : 10);
      const span = difficulty === 2 ? 4 : 6;
      const values: number[] = [];
      for (let v = low; v <= low + span; v += 1) {
        for (let i = 0; i < rng.int(1, 4); i += 1) values.push(v);
      }
      const asked = rng.int(low, low + span);
      const count = values.filter((v) => v === asked).length;
      return numericExercise({
        prompt: `How many pencils were ${asked} centimetres long?`,
        figure: { kind: 'dot-plot', values, from: low, to: low + span, label: 'length in cm' },
        unit: 'pencils',
        value: count,
        summary: `There are ${count} dots stacked above ${asked}, so ${count} pencils measured ${asked} cm.`,
        hint: 'Find the value along the bottom, then count upwards.',
        steps: [
          { label: 'Find the value on the axis.', latex: String(asked) },
          { label: 'Count the dots above it.', latex: String(count) },
        ],
        traps: [trap(asked, 'count-and-value-swapped')],
      });
    },
  }),

  template({
    id: 'line-plot-most-common',
    conceptId: 'line-plots',
    description: 'Identify the most frequent measurement on a line plot.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const low = rng.int(2, difficulty === 2 ? 5 : 9);
      const span = difficulty === 2 ? 3 : 5;
      const points = Array.from({ length: span + 1 }, (_, i) => ({ value: low + i, count: rng.int(1, 3) }));
      const winner = rng.int(0, span);
      // One clear mode, or "which was most common" has more than one answer.
      (points[winner] as { value: number; count: number }).count = 5;
      const values = points.flatMap((point) => Array.from({ length: point.count }, () => point.value));
      const mode = (points[winner] as { value: number; count: number }).value;
      const other = points.find((point) => point.value !== mode) as { value: number; count: number };
      const { choices, correctId } = options(
        [
          { label: `${mode} cm`, correct: true },
          { label: `${other.value} cm`, misconceptionId: 'pattern-rule-misread' },
          { label: '5 dots', misconceptionId: 'count-and-value-swapped' },
          { label: `${low + span + 1} cm` },
        ],
        rng.shuffle,
      );
      return choiceExercise({
        prompt: 'Which length was measured most often?',
        figure: { kind: 'dot-plot', values, from: low, to: low + span, label: 'length in cm' },
        choices,
        correctId,
        summary: `The tallest stack sits above ${mode}, with 5 dots — so ${mode} cm was measured most often.`,
        hint: 'Look for the tallest column of dots, then read the value underneath it.',
        steps: [
          { label: 'Find the tallest stack.', latex: '5 \\text{ dots}' },
          { label: 'Read the value below it.', latex: `${mode} \\text{ cm}` },
        ],
      });
    },
  }),

  template({
    id: 'line-plot-range',
    conceptId: 'line-plots',
    description: 'Find the spread between the largest and smallest measurement.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const low = rng.int(2, difficulty === 3 ? 7 : 12);
      const span = difficulty === 3 ? rng.int(3, 5) : rng.int(5, 9);
      const values: number[] = [low, low + span];
      for (let i = 0; i < 8; i += 1) values.push(rng.int(low, low + span));
      return numericExercise({
        prompt: 'What is the difference between the longest and the shortest measurement?',
        figure: { kind: 'dot-plot', values, from: low, to: low + span, label: 'length in cm' },
        unit: 'cm',
        value: span,
        summary: `The longest is ${low + span} cm and the shortest is ${low} cm, a difference of ${span} cm.`,
        hint: 'Find the leftmost dot and the rightmost dot, then subtract.',
        steps: [
          { label: 'Longest.', latex: String(low + span) },
          { label: 'Shortest.', latex: String(low) },
          { label: 'Subtract.', latex: `${low + span} - ${low} = ${span}` },
        ],
        traps: [
          trap(low + low + span, 'graph-total-for-difference'),
          ...(values.length !== low + low + span ? [trap(values.length, 'count-and-value-swapped')] : []),
        ],
      });
    },
  }),

  /* ------------------------------------------------ shape attributes ----- */
  template({
    id: 'polygon-parts',
    conceptId: 'shape-attributes',
    description: 'Count the sides, vertices or angles of a polygon.',
    difficulties: [1, 2],
    build: ({ rng, difficulty }) => {
      const sides = difficulty === 1 ? rng.int(3, 7) : rng.int(6, 12);
      const part = rng.pick(['sides', 'vertices (corners)', 'angles']);
      return numericExercise({
        prompt: `How many ${part} does this polygon have?`,
        figure: { kind: 'polygon', sides },
        value: sides,
        summary: `A polygon has as many corners and as many angles as it has sides — here, ${sides} of each.`,
        hint: 'Every side ends at a corner, and every corner holds one angle.',
        steps: [
          { label: 'Count the sides.', latex: String(sides) },
          { label: 'Corners and angles match them.', latex: String(sides) },
        ],
        traps: [trap(sides - 1, 'shape-named-by-look'), trap(sides + 1, 'shape-named-by-look')],
      });
    },
  }),

  template({
    id: 'name-the-polygon',
    conceptId: 'shape-attributes',
    description: 'Name a polygon from its number of sides.',
    difficulties: [1, 2],
    build: ({ rng, difficulty }) => {
      const names: Record<number, string> = {
        3: 'Triangle',
        4: 'Quadrilateral',
        5: 'Pentagon',
        6: 'Hexagon',
        7: 'Heptagon',
        8: 'Octagon',
      };
      const sides = difficulty === 1 ? rng.int(3, 6) : rng.int(5, 8);
      const wrong = rng.shuffle([3, 4, 5, 6, 7, 8].filter((n) => n !== sides)).slice(0, 3);
      const { choices, correctId } = options(
        [
          { label: names[sides] as string, correct: true },
          ...wrong.map((n) => ({ label: names[n] as string, misconceptionId: 'shape-named-by-look' })),
        ],
        rng.shuffle,
      );
      return choiceExercise({
        prompt: `What is a polygon with ${sides} sides called?`,
        figure: { kind: 'polygon', sides },
        choices,
        correctId,
        summary: `${sides} sides makes a ${(names[sides] as string).toLowerCase()}.`,
        hint: 'Count the sides in the picture, then match the count to the name.',
        steps: [{ label: 'Count the sides.', latex: String(sides) }],
      });
    },
  }),

  template({
    id: 'quadrilateral-classification',
    conceptId: 'shape-attributes',
    description: 'Decide whether one family of quadrilaterals contains another.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const claims = [
        { claim: 'Every square is a rectangle.', answer: true },
        { claim: 'Every rectangle is a square.', answer: false },
        { claim: 'Every square is a rhombus.', answer: true },
        { claim: 'Every rhombus is a square.', answer: false },
        { claim: 'Every rectangle is a quadrilateral.', answer: true },
        { claim: 'Every quadrilateral is a rectangle.', answer: false },
        { claim: 'Every square has four right angles.', answer: true },
        { claim: 'Every rhombus has four right angles.', answer: false },
      ];
      const picked = rng.pick(claims);
      const { choices, correctId } = options(
        [
          { label: 'True', correct: picked.answer, ...(picked.answer ? {} : { misconceptionId: 'square-not-a-rectangle' }) },
          { label: 'False', correct: !picked.answer, ...(picked.answer ? { misconceptionId: 'square-not-a-rectangle' } : {}) },
        ],
        rng.shuffle,
      );
      return choiceExercise({
        prompt: `True or false: ${picked.claim}`,
        choices,
        correctId,
        summary: picked.answer
          ? 'The attributes that define the wider family are all present, so every member of the smaller family belongs to it.'
          : 'One member is enough to break it: a shape can belong to the wider family without having the extra attribute.',
        hint: 'Write down what each name requires, then check whether one list contains the other.',
        steps: [
          { label: 'List what each name requires.', latex: '\\text{sides, angles, lengths}' },
          { label: picked.answer ? 'Every requirement is met.' : 'A requirement is missing.', latex: picked.answer ? '\\text{true}' : '\\text{false}' },
        ],
      });
    },
  }),

  template({
    id: 'shape-from-attributes',
    conceptId: 'shape-attributes',
    description: 'Name a quadrilateral from a list of its attributes.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const cases = [
        { description: 'four equal sides and four right angles', answer: 'Square', decoys: ['Rectangle', 'Rhombus', 'Trapezium'] },
        { description: 'four right angles, with the opposite sides equal', answer: 'Rectangle', decoys: ['Square', 'Rhombus', 'Parallelogram'] },
        { description: 'four equal sides, but no right angles', answer: 'Rhombus', decoys: ['Square', 'Rectangle', 'Trapezium'] },
        { description: 'exactly one pair of parallel sides', answer: 'Trapezium', decoys: ['Parallelogram', 'Rectangle', 'Rhombus'] },
        { description: 'two pairs of parallel sides, but no right angles', answer: 'Parallelogram', decoys: ['Rectangle', 'Trapezium', 'Square'] },
      ];
      const picked = rng.pick(cases);
      const { choices, correctId } = options(
        [
          { label: picked.answer, correct: true },
          ...picked.decoys.map((label) => ({ label, misconceptionId: 'shape-named-by-look' })),
        ],
        rng.shuffle,
      );
      return choiceExercise({
        prompt: `A quadrilateral has ${picked.description}. What is it?`,
        choices,
        correctId,
        summary: `Those attributes together describe a ${picked.answer.toLowerCase()}.`,
        hint: 'Take the attributes one at a time and rule out every shape that fails one.',
        steps: [{ label: 'Match every attribute.', latex: `\\text{${picked.answer}}` }],
      });
    },
  }),

  /* ------------------------------------------------- equal shares -------- */
  template({
    id: 'unit-fraction-of-shape',
    conceptId: 'equal-shares',
    description: 'Name one equal part of a partitioned shape.',
    difficulties: [1, 2],
    build: ({ rng, difficulty }) => {
      const parts = rng.int(2, difficulty === 1 ? 6 : 12);
      const shape = rng.pick(['rectangle', 'ribbon', 'chocolate bar', 'paper strip']);
      return fractionExercise({
        prompt: `A ${shape} is cut into ${parts} equal parts. What fraction of the whole is one part?`,
        figure: { kind: 'fraction-bar', parts, shaded: 1 },
        value: rawFraction(1, parts),
        summary: `${parts} equal parts make the whole, so each one is one ${partName(parts)} of it.`,
        hint: 'How many equal parts is the whole cut into? That number goes underneath.',
        steps: [
          { label: 'Count the equal parts.', latex: String(parts) },
          { label: 'One of them.', latex: `\\frac{1}{${parts}}` },
        ],
        traps: [fractionTrap(rawFraction(parts, 1), 'parts-not-equal')],
      });
    },
  }),

  template({
    id: 'equal-shares-remaining',
    conceptId: 'equal-shares',
    description: 'Find the fraction of a shape that is left.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const parts = rng.int(4, difficulty === 2 ? 8 : 12);
      const taken = rng.int(1, parts - 1);
      return fractionExercise({
        prompt: `A pizza is cut into ${parts} equal slices and ${taken} are eaten. What fraction of the pizza is left?`,
        figure: { kind: 'fraction-bar', parts, shaded: parts - taken },
        value: rawFraction(parts - taken, parts),
        summary: `${parts} − ${taken} = ${parts - taken}, so ${parts - taken} ${parts - taken === 1 ? 'slice is' : 'slices are'} left out of ${parts}.`,
        hint: 'The denominator does not change — eating a slice does not resize the others.',
        steps: [
          { label: 'Slices left.', latex: `${parts} - ${taken} = ${parts - taken}` },
          { label: 'Out of the original number.', latex: `\\frac{${parts - taken}}{${parts}}` },
        ],
        traps: [
          fractionTrap(rawFraction(taken, parts), 'parts-not-equal'),
          fractionTrap(rawFraction(parts - taken, parts - taken), 'whole-number-fraction-confused'),
        ],
      });
    },
  }),

  template({
    id: 'grid-equal-squares',
    conceptId: 'equal-shares',
    description: 'Name one square of a rectangle split into a grid.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const rows = rng.int(2, difficulty === 2 ? 4 : 6);
      const columns = rng.int(2, difficulty === 2 ? 5 : 7);
      return fractionExercise({
        prompt: `A rectangle is split into ${rows} rows and ${columns} columns of equal squares. What fraction of the rectangle is one square?`,
        figure: { kind: 'area-model', rows, columns, shadedRows: 1, shadedColumns: 1 },
        value: rawFraction(1, rows * columns),
        summary: `${rows} rows of ${columns} makes ${rows * columns} equal squares, so one square is 1 out of ${rows * columns}.`,
        hint: 'Count the squares, do not count the rows and columns separately.',
        steps: [
          { label: 'Count all the squares.', latex: `${rows} \\times ${columns} = ${rows * columns}` },
          { label: 'One of them.', latex: `\\frac{1}{${rows * columns}}` },
        ],
        traps: [fractionTrap(rawFraction(1, rows + columns), 'parts-not-equal')],
      });
    },
  }),

  /* --------------------------------------------- area of composites ------ */
  template({
    id: 'l-shape-area',
    conceptId: 'area-composite',
    description: 'Find the area of an L-shape by subtracting the missing corner.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const width = rng.int(4, difficulty === 3 ? 8 : 12);
      const height = rng.int(3, difficulty === 3 ? 6 : 9);
      const cutWidth = rng.int(1, width - 2);
      const cutHeight = rng.int(1, height - 2);
      const shaded: [number, number][] = [];
      for (let row = 0; row < height; row += 1) {
        for (let column = 0; column < width; column += 1) {
          if (row < cutHeight && column >= width - cutWidth) continue;
          shaded.push([column, row]);
        }
      }
      const area = width * height - cutWidth * cutHeight;
      return numericExercise({
        prompt: 'Each square is one square centimetre. What is the area of the shaded shape?',
        figure: { kind: 'grid', width, height, shaded },
        unit: 'cm²',
        value: area,
        summary: `The whole rectangle is ${width} × ${height} = ${width * height}, and the missing corner is ${cutWidth} × ${cutHeight} = ${cutWidth * cutHeight}.`,
        hint: 'Find the area of the rectangle it would be if the corner were filled in, then take the corner away.',
        steps: [
          { label: 'The surrounding rectangle.', latex: `${width} \\times ${height} = ${width * height}` },
          { label: 'The missing corner.', latex: `${cutWidth} \\times ${cutHeight} = ${cutWidth * cutHeight}` },
          { label: 'Subtract.', latex: `${width * height} - ${cutWidth * cutHeight} = ${area}` },
        ],
        traps: [
          trap(width * height, 'composite-whole-rectangle'),
          ...(2 * (width + height) !== width * height ? [trap(2 * (width + height), 'perimeter-for-area')] : []),
        ],
      });
    },
  }),

  template({
    id: 'two-rectangles-area',
    conceptId: 'area-composite',
    description: 'Add the areas of the two rectangles a shape is made from.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const ceiling = difficulty === 2 ? 8 : 14;
      const a = rng.int(2, ceiling);
      const b = rng.int(2, ceiling);
      const c = rng.int(2, ceiling);
      const d = rng.int(2, ceiling);
      return numericExercise({
        prompt: `A shape is made by joining a ${a} cm by ${b} cm rectangle to a ${c} cm by ${d} cm rectangle. What is its total area?`,
        unit: 'cm²',
        value: a * b + c * d,
        summary: `${a} × ${b} = ${a * b} and ${c} × ${d} = ${c * d}; together that is ${a * b + c * d} cm².`,
        hint: 'Find each rectangle on its own, then add. Areas of separate pieces always add.',
        steps: [
          { label: 'First rectangle.', latex: `${a} \\times ${b} = ${a * b}` },
          { label: 'Second rectangle.', latex: `${c} \\times ${d} = ${c * d}` },
          { label: 'Add the two areas.', latex: `${a * b} + ${c * d} = ${a * b + c * d}` },
        ],
        traps: [
          trap(a * b, 'intermediate-answer-given'),
          trap((a + c) * (b + d), 'composite-whole-rectangle'),
        ],
      });
    },
  }),

  template({
    id: 'area-split-into-parts',
    conceptId: 'area-composite',
    description: 'Use the area model of the distributive property.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const height = rng.int(3, difficulty === 3 ? 7 : 9);
      const tens = 10;
      const ones = rng.int(2, 9);
      const width = tens + ones;
      return numericExercise({
        prompt: `A rectangle is ${height} cm tall and ${width} cm wide. It is cut into a ${height} by ${tens} piece and a ${height} by ${ones} piece. What is the area of the whole rectangle?`,
        figure: { kind: 'area-model', rows: height, columns: width, shadedRows: height, shadedColumns: tens },
        unit: 'cm²',
        value: height * width,
        summary: `${height} × ${tens} = ${height * tens} and ${height} × ${ones} = ${height * ones}, so the whole is ${height * width} cm².`,
        hint: 'Cutting a rectangle does not change how much surface there is. Add the two pieces.',
        steps: [
          { label: 'The shaded piece.', latex: `${height} \\times ${tens} = ${height * tens}` },
          { label: 'The remaining piece.', latex: `${height} \\times ${ones} = ${height * ones}` },
          { label: 'Add them.', latex: `${height * tens} + ${height * ones} = ${height * width}` },
        ],
        traps: [
          trap(height * tens, 'partial-product-dropped'),
          trap(height * tens + ones, 'partial-product-dropped'),
        ],
      });
    },
  }),
];
